import React from 'react';
import { Spinner, Stack, Text, Box } from '@solvhealth/jigsaw-v1';
import { TFunction, useTranslation } from 'react-i18next';
import { ActiveBookingStates } from '~/components/AccountGetsMarried/components/GetCare/components/AccountHeadline/components/AppointmentCard/util';

type Props = {
  bookingState: number;
  patientName: string;
  locationName: string;
  isLoading?: boolean;
};

const descriptiveTextCopy = (args: {
  bookingState: ActiveBookingStates;
  patientName: string;
  locationName: string;
  t: TFunction<'account'[]>;
}) => {
  const { bookingState, patientName, locationName, t } = args;
  switch (bookingState) {
    case ActiveBookingStates.PreTelemedIncompleteAndEarly:
      return t('account:appointmentCard.descriptiveText.completePaperworkBeforeCall');
    case ActiveBookingStates.PreExternalTelemed:
      return t('account:appointmentCard.descriptiveText.waitForExternalTelemed', { locationName });
    case ActiveBookingStates.PreTelemedIncompleteAndApptSoon:
    case ActiveBookingStates.PreTelemedCompleteAndApptSoon:
    case ActiveBookingStates.InTelemed:
      return t('account:appointmentCard.descriptiveText.waitForProvider');
    case ActiveBookingStates.PreTelemedCompleteAndEarly:
      return t('account:appointmentCard.descriptiveText.joinBeforeScheduledStart');
    case ActiveBookingStates.PreClinicVisitNoCheckIncomplete:
    case ActiveBookingStates.PreClinicVisitHasCheckIncompleteAndEarly:
      return t('account:appointmentCard.descriptiveText.completePaperworkBeforeArrival');
    case ActiveBookingStates.PreClinicVisitNoCheckComplete:
      return t('account:appointmentCard.descriptiveText.yourPaperworkSent');
    case ActiveBookingStates.PreClinicVisitNoCheckNAPaperwork:
      return t('account:appointmentCard.descriptiveText.directionsRescheduleCancel');
    case ActiveBookingStates.PreClinicVisitHasCheckCompleteOrApptSoon:
      return t('account:appointmentCard.descriptiveText.checkInAtArrival');
    case ActiveBookingStates.InClinicVisit:
      return t('account:appointmentCard.descriptiveText.checkedIn');
    case ActiveBookingStates.PostTelemedSummaryPending:
      return t('account:appointmentCard.descriptiveText.waitingForVisitSummary');
    case ActiveBookingStates.PostTelemedSummaryDownloadable:
      return t('account:appointmentCard.descriptiveText.downloadSummary');
    case ActiveBookingStates.PostTelemedSummaryUpdatable:
      return t('account:appointmentCard.descriptiveText.updateSummary');
    case ActiveBookingStates.PostTelemedSummaryViewable:
      return t('account:appointmentCard.descriptiveText.viewSummary');
    case ActiveBookingStates.PostTelemedNoRating:
    case ActiveBookingStates.PostClinicVisitNoRating:
      return t('account:appointmentCard.descriptiveText.letUsKnow');
    case ActiveBookingStates.PostTelemedHasRating:
    case ActiveBookingStates.PostClinicVisitHasRating:
      return t('account:appointmentCard.descriptiveText.takeNote');
    case ActiveBookingStates.PostVisitHasLabResult:
      return t('account:appointmentCard.descriptiveText.testResultsAvailable', { patientName });
    default:
      return '';
  }
};

const DescriptiveText = (props: Props) => {
  const { isLoading, patientName, locationName, bookingState } = props;
  const { t } = useTranslation(['account']);

  return isLoading ? (
    <Box height="20px" />
  ) : (
    <Text muted variant="body2">
      {descriptiveTextCopy({ bookingState, patientName, locationName, t })}
    </Text>
  );
};

export default DescriptiveText;
